import { forwardRef } from "react";
import { ButtonToggle, ButtonToggleProps } from "../../../Button";
import { WithAriaLabelEnforcement } from "../../sharedTypes";
import { ToolbarItemWrapper } from "../ToolbarItemWrapper/ToolbarItemWrapper";
import { useOverFlowItemContext } from "../ToolbarOverflowMenu/ToolbarOverflowMenu";

import cx from "classnames";
import styles from "../../Toolbar.module.scss";
import { Tooltip } from "../../../Tooltip";

/**
 * Base props for the ToolbarButtonToggle component
 * @extends Omit<ButtonToggleProps, "aria-label" | "children">
 */
type ToolbarButtonToggleBaseProps = Omit<
  ButtonToggleProps,
  "aria-label" | "children"
>;

/**
 * Props for the ToolbarButtonToggle component
 * @extends WithAriaLabelEnforcement<ToolbarButtonToggleBaseProps>
 */
type ToolbarButtonToggleProps =
  WithAriaLabelEnforcement<ToolbarButtonToggleBaseProps>;

/**
 * ToolbarButtonToggle component for toggleable actions within a toolbar.
 *
 * Features:
 * - Toggle state management (on/off)
 * - Designed specifically for toolbar integration
 * - Consistent styling with other toolbar items
 * - Automatic tooltip for icon-only toggles
 * - Overflow menu support
 * - Accessibility enforcement for aria-labels
 * - Small size optimized for toolbar layout
 * - Visual feedback for toggle state
 * - Proper ARIA attributes for toggle semantics
 *
 * @example
 * <ToolbarButtonToggle icon={BoldIcon} aria-label="Bold" checked={isBold} />
 *
 * @example
 * <ToolbarButtonToggle checked={isItalic}>
 *   Italic
 * </ToolbarButtonToggle>
 */
const ToolbarButtonToggle = forwardRef<
  HTMLButtonElement,
  ToolbarButtonToggleProps
>((props, _ref) => {
  const { className, children, ...rest } = props;

  // Checks if this component is rendered inside the overflow menu and fallbacks when the context is unavailable
  const { isInOverflowMenu = false } = useOverFlowItemContext?.() || {};

  const buttonToggleClassNames = cx(
    className,
    styles["toolbar-button-item"],
    styles["toolbar-button-toggle-item"],
  );

  const item = children ? (
    <ButtonToggle
      size="small"
      data-anv="toolbar-button-toggle"
      className={buttonToggleClassNames}
      {...rest}
    >
      {children}
    </ButtonToggle>
  ) : (
    <Tooltip>
      <Tooltip.Trigger>
        <ButtonToggle
          size="small"
          data-anv="toolbar-button-toggle"
          className={buttonToggleClassNames}
          {...rest}
        />
      </Tooltip.Trigger>
      <Tooltip.Content>{rest["aria-label"]}</Tooltip.Content>
    </Tooltip>
  );

  if (isInOverflowMenu) {
    return item;
  }

  return (
    <ToolbarItemWrapper item={{ itemProps: props, itemType: "buttonToggle" }}>
      {item}
    </ToolbarItemWrapper>
  );
});

ToolbarButtonToggle.displayName = "ToolbarButtonToggle";

export { type ToolbarButtonToggleProps, ToolbarButtonToggle };
